import React, { useState } from "react";
import Papa from "papaparse";
// import * as XLSX from "xlsx";

import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  Tooltip
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

/* ---------------------------------------------
 CONFIG
---------------------------------------------- */

interface BulkCreativeUploadProps {
  scripts: any[];
  setScripts: React.Dispatch<React.SetStateAction<any[]>>;
  onBulkConfirm: (scripts: any[], replace: boolean) => void;
}

const EXPECTED_HEADERS = ["headline", "body", "cta"];

/* ---------------------------------------------
 COMPONENTE PRINCIPAL
---------------------------------------------- */

const BulkCreativeUpload: React.FC<BulkCreativeUploadProps> = ({
  scripts,
  setScripts,
  onBulkConfirm
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState<string | null>(null);
  const [pasteInput, setPasteInput] = useState("");

  /* ---------------------------------------------
    NORMALIZAÇÃO DOS DADOS
  ---------------------------------------------- */

  const normalizeRows = (rows: string[][]) => {
    if (!rows.length) return [];

    const header = rows[0].map((h) => h.toLowerCase().trim());
    const isValidHeader = EXPECTED_HEADERS.every((h) => header.includes(h));

    if (isValidHeader) {
      return [EXPECTED_HEADERS, ...rows.slice(1)];
    }

    return [EXPECTED_HEADERS, ...rows];
  };

  /* ---------------------------------------------
    CSV / XLSX
  ---------------------------------------------- */

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setShowError(null);

    try {
      if (file.name.endsWith(".csv")) {
        Papa.parse(file, {
          complete: (res) => {
            const parsed = normalizeRows(res.data as string[][]);
            setPreviewData(parsed);
            setLoading(false);
          }
        });
      }

      // .xlsx removido
    } catch (err) {
      setShowError("Failed to process file.");
      setLoading(false);
    }
  };

  /* ---------------------------------------------
    COLAR CSV
  ---------------------------------------------- */

  const handlePasteCSV = () => {
    if (!pasteInput.trim()) return;

    const parsed = Papa.parse(pasteInput.trim()).data as string[][];
    const normalized = normalizeRows(parsed);
    setPreviewData(normalized);
  };

  /* ---------------------------------------------
    CONFIRMAR (ADD OU REPLACE)
  ---------------------------------------------- */

  const confirm = (replace = false) => {
    if (!previewData.length) return;

    const headers = previewData[0].map((h) => h.toLowerCase().trim());
    const rows = previewData.slice(1);

    const mapping: any = {};
    EXPECTED_HEADERS.forEach((h) => {
      const idx = headers.indexOf(h);
      if (idx !== -1) mapping[h] = idx;
    });

    if (Object.keys(mapping).length < 3) {
      setShowError("Could not detect Headline, Body and CTA columns.");
      return;
    }

    const newScripts = rows
      .filter((r) => r.some((c) => c.trim() !== ""))
      .map((row) => ({
        id: crypto.randomUUID(),
        headline: row[mapping.headline] || "",
        body: row[mapping.body] || "",
        cta: row[mapping.cta] || "",
        fullScript: `${row[mapping.headline] || ""}\n\n${row[mapping.body] || ""}\n\n${row[mapping.cta] || ""}`,
        status: "pending"
      }));

    onBulkConfirm(newScripts, replace);

    setPreviewData([]);
    setPasteInput("");
    setModalOpen(false);
  };

  /* ---------------------------------------------
    UI
  ---------------------------------------------- */

  return (
    <>
      <IconButton onClick={() => setModalOpen(true)} sx={{ color: "#FFBF00" }}>
        <UploadFileIcon />
      </IconButton>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Bulk Creative Upload</DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            {/* CSV Upload */}
            <Tooltip title="Upload CSV">
              <IconButton component="label" sx={{ color: "#FFBF00" }}>
                <UploadFileIcon />
                <input type="file" hidden accept=".csv" onChange={handleFileUpload} />
              </IconButton>
            </Tooltip>

            {/* REPLACE ALL */}
            {scripts.length > 0 && previewData.length > 0 && (
              <Tooltip title="Replace all scripts">
                <IconButton onClick={() => confirm(true)} sx={{ bgcolor: "red", color: "white" }}>
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* PASTE CSV */}
          <TextField
            label="Paste CSV Content"
            multiline
            minRows={3}
            fullWidth
            value={pasteInput}
            onChange={(e) => setPasteInput(e.target.value)}
            InputLabelProps={{
              shrink: true,
              sx: {
                color: "black",
                "&.Mui-focused": {
                  color: "#FFBF00"
                }
              }
            }}
          />

          <Button
            startIcon={<ContentPasteIcon />}
            sx={{ mt: 1, color: "#FFBF00" }}
            onClick={handlePasteCSV}
          >
            Parse Pasted CSV
          </Button>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {showError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {showError}
            </Alert>
          )}

          {/* PREVIEW */}
          {previewData.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 300, mt: 2 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {previewData[0].map((h, i) => (
                      <TableCell key={i}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {previewData.slice(1).map((row, idx) => (
                    <TableRow key={idx}>
                      {row.map((c, j) => (
                        <TableCell key={j}>{c}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Close</Button>

          <Button
            disabled={previewData.length === 0}
            variant="contained"
            onClick={() => confirm(false)}
          >
            Add Scripts
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkCreativeUpload;
